/*!

=========================================================
* Black Dashboard React v1.2.2
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/black-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";


// reactstrap components
import {
  NavbarBrand,
  Navbar,
  NavLink,
  Container,
  NavbarToggler,
  Button,
} from "reactstrap";

// Import utility functions
import { removeCookie } from "../../views/utils";

function AdminNavbar(props) {
  const [collapseOpen, setcollapseOpen] = React.useState(false);
  const [screenHeight, setScreenHeight] = React.useState(window.innerHeight);
  const location = useLocation();
  const navigate = useNavigate();
  
  // Handle window resize to adjust menu height
  React.useEffect(() => {
    const handleResize = () => {
      setScreenHeight(window.innerHeight);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // this function opens and closes the collapse on small devices
  const toggleCollapse = () => {
    // Simplified toggle without color changes for better performance
    setcollapseOpen(!collapseOpen);
  };

  // Function to handle sign out
  const handleSignOut = () => {
    // Remove the authentication cookie
    const brandname = "Tracklist Event Matching";
    const cookieName = `${brandname}_token`;
    
    // Use the proper removeCookie function
    removeCookie(cookieName);
    
    // Close mobile menu if open
    setcollapseOpen(false);
    
    // Redirect to home page
    navigate('/admin/features');
    
    // Optionally reload the page to clear any cached state
    window.location.reload();
  };

  // Memoized display name mapping for better performance
  const displayNameMap = React.useMemo(() => ({
    "Home": "Home",
    "How this site works": "How it Works",
    "Premium": "Premium",
    "About": "About",
    "Privacy": "Privacy",
    "Cookies": "Cookies",
    "FAQ": "FAQ"
  }), []);

  // Generate menu items with proper navigation
  const menuItems = React.useMemo(() => {
    if (!props.routes) return [];
    
    return props.routes.map((route) => {
      const displayName = displayNameMap[route.name] || route.name;
      const isActive = location.pathname === (route.layout + route.path);
      
      return (
        <li key={route.path} className="nav-item">
          <Link
            to={route.layout + route.path}
            className={`nav-link-horizontal ${isActive ? 'active' : ''}`}
            onClick={() => setcollapseOpen(false)}
          >
            <span className="nav-link-text">{displayName}</span>
          </Link>
        </li>
      );
    });
  }, [props.routes, displayNameMap, location.pathname]);

  // Memoize the sign out button for better performance
  const signOutButton = React.useMemo(() => (
    <li className="nav-item">
      <Button
        color="link"
        className="nav-link-horizontal sign-out-btn"
        onClick={handleSignOut}
      >
        <span className="nav-link-text">Sign Out</span>
      </Button>
    </li>
  ), [handleSignOut]);



  return (
    <>
      <Navbar className="navbar-absolute navbar-transparent" expand="lg">
        <Container fluid>
          <div className="navbar-wrapper">
            <NavbarBrand href="#pablo" onClick={(e) => e.preventDefault()}>
              <span className="navbar-brand-text">{props.brandText}</span>
            </NavbarBrand>
          </div>
          
          {/* Hamburger Menu Toggle */}
          <NavbarToggler 
            onClick={toggleCollapse}
            type="button"
            aria-label="Toggle navigation"
            aria-expanded={collapseOpen}
            aria-controls="navbar-collapse"
          >
            <span className="navbar-toggler-bar bar1" />
            <span className="navbar-toggler-bar bar2" />
            <span className="navbar-toggler-bar bar3" />
          </NavbarToggler>
          
          {/* Navigation Menu - Show on desktop, conditional on mobile */}
          <div 
            className={`navbar-collapse ${collapseOpen ? 'show' : ''}`} 
            id="navbar-collapse"
            style={{
              maxHeight: collapseOpen ? `${screenHeight - 80}px` : 'none',
              overflowY: collapseOpen ? 'auto' : 'visible'
            }}
          >
            <ul className="navbar-nav navbar-nav-horizontal">
              {menuItems}
              {signOutButton}
            </ul>
          </div>
        </Container>
      </Navbar>
    </>
  );
}

export default AdminNavbar;
